import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ClipboardList, BadgeEuro, Truck, Check, ArrowLeft, ArrowRight, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Reveal, EASE_OUT } from "@/components/motion/Reveal";
import { createTradeIn } from "@/lib/api";
import { CATEGORY_LABELS } from "@/data/content";
import { cn } from "@/lib/utils";

const BRANDS = ["APPLE", "SAMSUNG", "SONY", "BOSE", "BEATS", "CANON", "DYSON", "Altro"];

const MACRO_STEPS = [
  { icon: ClipboardList, title: "Compila il form", text: "Raccontaci del tuo dispositivo." },
  { icon: BadgeEuro, title: "Ricevi la valutazione", text: "Entro 24-48h via email." },
  { icon: Truck, title: "Spedisci e vieni pagato", text: "Spedizione gratuita, paghi dopo verifica." },
];

const STEPS = [
  { n: 1, label: "Dispositivo", title: "Tipo di prodotto e marca" },
  { n: 2, label: "Modello", title: "Modello e versione" },
  { n: 3, label: "Condizioni", title: "Condizioni del dispositivo" },
  { n: 4, label: "Contatti", title: "Dati di contatto" },
  { n: 5, label: "Foto e note", title: "Foto e note (facoltative)" },
];

const BATTERY_OPTS = ["Ottima", "Buona", "Da sostituire", "Non so"];
const COSMETIC_OPTS = ["Nessun segno", "Segni minimi", "Segni evidenti"];

const TOTAL_STEPS = STEPS.length;
const INITIAL_FORM = {
  product_type: "", brand: "", model: "", capacity: "",
  powers_on: "", screen_intact: "", battery: "", cosmetic: "",
  full_name: "", email: "", phone: "", city: "", notes: "",
};

const slide = {
  enter: (dir) => ({ x: dir * 48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir * -48, opacity: 0 }),
};

const pad = (n) => String(n).padStart(2, "0");

export default function TradeIn() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 1) return form.product_type && form.brand;
    if (step === 2) return form.model.trim();
    if (step === 3) return form.powers_on && form.screen_intact && form.battery && form.cosmetic;
    if (step === 4) return form.full_name.trim() && form.email.trim() && form.phone.trim() && form.city.trim();
    return true;
  };

  const next = () => {
    if (!validateStep()) {
      toast.error("Compila i campi richiesti per continuare");
      return;
    }
    setDirection(1);
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const back = () => {
    setDirection(-1);
    setStep((s) => Math.max(1, s - 1));
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Immagine troppo grande (max 5MB)");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, { name: file.name, data: reader.result }]);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createTradeIn({ ...form, photos });
      setSubmitted(true);
    } catch (err) {
      toast.error("Errore durante l'invio", { description: "Riprova tra poco." });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setStep(1);
    setDirection(1);
    setForm(INITIAL_FORM);
    setPhotos([]);
  };

  if (submitted) {
    return (
      <div className="container-page section" data-testid="tradein-success">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE_OUT }}>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-lime text-ink">
              <Check className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="eyebrow mt-8">Richiesta ricevuta</div>
            <h1 className="display-xl mt-4 text-foreground">Grazie {form.full_name.split(" ")[0]}, ci pensiamo noi.</h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              Abbiamo ricevuto la tua richiesta di valutazione per <span className="text-foreground">{form.brand} {form.model}</span>.
            </p>
            <Button variant="outline" className="mt-10" onClick={reset} data-testid="tradein-reset-button">
              Valuta un altro dispositivo
            </Button>
          </motion.div>
          <motion.ol
            className="divide-y divide-border border-y border-border lg:col-span-5 lg:self-end"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_OUT, delay: 0.15 }}
          >
            {[
              { n: "01", t: "Valutazione entro 24-48h", d: `Ti invieremo la nostra offerta via email a ${form.email}.` },
              { n: "02", t: "Spedizione gratuita", d: "Se accetti, ricevi un'etichetta prepagata per spedire senza costi." },
              { n: "03", t: "Pagamento dopo verifica", d: "Verifichiamo il dispositivo e ti paghiamo rapidamente." },
            ].map((s) => (
              <li key={s.n} className="flex gap-6 py-6">
                <span className="font-mono text-sm text-lime">{s.n}</span>
                <div>
                  <div className="font-display text-lg font-semibold tracking-tightest text-foreground">{s.t}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{s.d}</div>
                </div>
              </li>
            ))}
          </motion.ol>
        </div>
      </div>
    );
  }

  const current = STEPS[step - 1];
  const progress = step / TOTAL_STEPS;

  return (
    <div className="container-page pb-24 pt-10 sm:pt-14" data-testid="tradein-page">
      <Reveal className="max-w-3xl">
        <div className="eyebrow">Valuta il tuo dispositivo</div>
        <h1 className="display-xl mt-4 text-foreground">Vendi il tuo dispositivo. Semplice, trasparente, senza costi.</h1>
        <p className="mt-5 max-w-xl text-lg text-muted-foreground">Un percorso guidato in cinque passi. Ricevi la valutazione entro 24-48h.</p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-14">
        {/* Indice step (sticky) */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-[100px]">
            <ol className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-0 lg:divide-y lg:divide-border lg:border-y lg:border-border lg:pb-0" data-testid="tradein-step-indicator">
              {STEPS.map((s) => {
                const done = s.n < step;
                const active = s.n === step;
                return (
                  <li
                    key={s.n}
                    className={cn(
                      "flex shrink-0 items-center gap-4 rounded border px-3 py-2.5 lg:rounded-none lg:border-0 lg:px-0 lg:py-4",
                      active ? "border-lime" : "border-border"
                    )}
                    data-testid={`tradein-step-${s.n}`}
                    data-state={active ? "active" : done ? "done" : "upcoming"}
                  >
                    <span
                      className={cn(
                        "flex h-6 w-6 items-center justify-center rounded-full border font-mono text-[10px] transition-[background-color,border-color,color] duration-300",
                        done ? "border-lime bg-lime text-ink" : active ? "border-lime text-lime" : "border-border text-muted-foreground"
                      )}
                    >
                      {done ? <Check className="h-3 w-3" strokeWidth={3} /> : pad(s.n)}
                    </span>
                    <span className={cn("text-sm transition-colors duration-300", active ? "text-foreground" : "text-muted-foreground")}>{s.label}</span>
                  </li>
                );
              })}
            </ol>

            <div className="mt-10 hidden lg:block">
              <div className="mono-label text-muted-foreground">Come funziona</div>
              <ul className="mt-4 space-y-4">
                {MACRO_STEPS.map((m, i) => {
                  const Icon = m.icon;
                  return (
                    <li key={i} className="flex gap-4">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border">
                        <Icon className="h-4 w-4 text-lime" strokeWidth={1.5} />
                      </span>
                      <div>
                        <div className="text-sm font-medium text-foreground">{m.title}</div>
                        <div className="text-xs text-muted-foreground">{m.text}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-8">
          <div className="rounded border border-border bg-card" data-testid="tradein-form-card">
            <div className="px-6 pt-6 sm:px-8 sm:pt-8">
              <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-mono text-muted-foreground">
                <span data-testid="tradein-step-label">Step {pad(step)} / {pad(TOTAL_STEPS)}</span>
                <span data-testid="tradein-progress-percent">{Math.round(progress * 100)}%</span>
              </div>
              <div className="mt-3 h-[2px] w-full overflow-hidden rounded-full bg-border" data-testid="tradein-progress">
                <motion.div
                  className="h-full w-full origin-left bg-lime"
                  initial={false}
                  animate={{ scaleX: progress }}
                  transition={{ type: "spring", stiffness: 140, damping: 22 }}
                />
              </div>
            </div>

            <div className="relative overflow-hidden px-6 pb-2 pt-8 sm:px-8">
              <AnimatePresence mode="wait" custom={direction} initial={false}>
                <motion.div
                  key={step}
                  custom={direction}
                  variants={slide}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease: EASE_OUT }}
                  className="min-h-[300px]"
                  data-testid={`tradein-step-content-${step}`}
                >
                  <h2 className="display-lg text-foreground">{current.title}</h2>

                  {step === 1 && (
                    <div className="mt-8 space-y-6">
                      <Field label="Tipo di prodotto" required>
                        <Select value={form.product_type} onValueChange={(v) => set("product_type", v)}>
                          <SelectTrigger className="h-11" data-testid="tradein-product-type"><SelectValue placeholder="Seleziona una categoria" /></SelectTrigger>
                          <SelectContent className="border-line bg-surface">
                            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Marca" required>
                        <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
                          <SelectTrigger className="h-11" data-testid="tradein-brand"><SelectValue placeholder="Seleziona un marchio" /></SelectTrigger>
                          <SelectContent className="border-line bg-surface">
                            {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="mt-8 space-y-6">
                      <Field label="Modello" required htmlFor="model">
                        <Input id="model" value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Es. iPhone 13 Pro" data-testid="tradein-model" />
                      </Field>
                      <Field label="Capacità / versione" htmlFor="capacity">
                        <Input id="capacity" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="Es. 128 GB, colore, taglia" data-testid="tradein-capacity" />
                      </Field>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="mt-8 space-y-7">
                      <YesNo label="Si accende e funziona?" value={form.powers_on} onChange={(v) => set("powers_on", v)} testid="tradein-powers-on" />
                      <YesNo label="Lo schermo è integro (nessuna crepa)?" value={form.screen_intact} onChange={(v) => set("screen_intact", v)} testid="tradein-screen" />
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Field label="Stato della batteria" required>
                          <Select value={form.battery} onValueChange={(v) => set("battery", v)}>
                            <SelectTrigger className="h-11" data-testid="tradein-battery"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent className="border-line bg-surface">{BATTERY_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </Field>
                        <Field label="Segni estetici" required>
                          <Select value={form.cosmetic} onValueChange={(v) => set("cosmetic", v)}>
                            <SelectTrigger className="h-11" data-testid="tradein-cosmetic"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent className="border-line bg-surface">{COSMETIC_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </Field>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Field label="Nome e cognome" required htmlFor="tn"><Input id="tn" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="tradein-name" /></Field>
                      </div>
                      <Field label="Email" required htmlFor="te"><Input id="te" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="tradein-email" /></Field>
                      <Field label="Telefono" required htmlFor="tp"><Input id="tp" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="tradein-phone" /></Field>
                      <div className="sm:col-span-2">
                        <Field label="Città" required htmlFor="tc"><Input id="tc" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="tradein-city" /></Field>
                      </div>
                    </div>
                  )}

                  {step === 5 && (
                    <div className="mt-8 space-y-6">
                      <Field label={`Carica fino a 4 foto · ${photos.length}/4`}>
                        <label
                          className="flex cursor-pointer flex-col items-center justify-center rounded border border-dashed border-border bg-background/40 p-8 text-center transition-colors duration-200 hover:border-lime/60"
                          data-testid="tradein-photo-dropzone"
                        >
                          <Upload className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                          <span className="mt-3 text-sm text-muted-foreground">Trascina o clicca per selezionare le immagini</span>
                          <span className="mt-1 font-mono text-[10px] uppercase tracking-mono text-muted-foreground/70">JPG · PNG · max 5 MB</span>
                          <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} disabled={photos.length >= 4} />
                        </label>
                        {photos.length > 0 && (
                          <div className="mt-3 grid grid-cols-4 gap-3">
                            {photos.map((p, i) => (
                              <div key={i} className="relative overflow-hidden rounded border border-border">
                                <img src={p.data} alt={p.name} className="aspect-square w-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                                  className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink/80 text-chalk backdrop-blur"
                                  aria-label="Rimuovi foto"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </Field>
                      <Field label="Note libere" htmlFor="notes">
                        <Textarea id="notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} placeholder="Aggiungi qualsiasi dettaglio utile alla valutazione" className="rounded-md border-input bg-background/40 focus-visible:ring-1 focus-visible:ring-lime" data-testid="tradein-notes" />
                      </Field>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigazione */}
            <div className="flex items-center justify-between border-t border-border px-6 py-5 sm:px-8">
              <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="tradein-back">
                <ArrowLeft strokeWidth={1.5} /> Indietro
              </Button>
              {step < TOTAL_STEPS ? (
                <Button variant="lime" size="lg" onClick={next} data-testid="tradein-next">
                  Continua <ArrowRight strokeWidth={2} />
                </Button>
              ) : (
                <Button variant="lime" size="lg" onClick={handleSubmit} disabled={submitting} data-testid="tradein-submit-button">
                  {submitting ? "Invio in corso" : "Invia richiesta"} <Check strokeWidth={2} />
                </Button>
              )}
            </div>
          </div>

          {/* Macro step mobile */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:hidden">
            {MACRO_STEPS.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="flex gap-3 rounded border border-border p-4">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-lime" strokeWidth={1.5} />
                  <div>
                    <div className="text-sm font-medium text-foreground">{m.title}</div>
                    <div className="text-xs text-muted-foreground">{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, htmlFor, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mono-label text-muted-foreground">
        {label}{required && <span className="ml-1 text-lime">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="mono-label text-muted-foreground">{label}<span className="ml-1 text-lime">*</span></Label>
      <div role="radiogroup" aria-label={label} className="mt-2 grid grid-cols-2 gap-3" data-testid={testid}>
        {[{ v: "si", l: "Sì" }, { v: "no", l: "No" }].map((opt) => {
          const sel = value === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              role="radio"
              aria-checked={sel}
              onClick={() => onChange(opt.v)}
              id={`${testid}-${opt.v}`}
              className={cn(
                "flex h-11 items-center justify-center gap-2 rounded border text-sm transition-[border-color,background-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                sel ? "border-lime bg-lime/[0.08] text-foreground" : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              )}
              data-testid={`${testid}-${opt.v}`}
            >
              <span className={cn("h-2 w-2 rounded-full transition-colors duration-200", sel ? "bg-lime" : "bg-border")} />
              {opt.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}
